// One user, many orgs, one session (#466 § 4). `/v1/me`'s memberships list every
// org the user belongs to; `reachable` says whether THIS session may open one
// (an SSO-minted session is pinned to the orgs whose IdP signed it in). The
// switcher and the bare-path landing both ask the questions below, so they live
// once here.

import { withReturnTo } from './return-to';
import type { MembershipView } from './session';

/**
 * Whether this session may open `m`. Only an explicit `false` closes it: the
 * API sends `reachable` on every membership, and an older `/v1/me` that does not
 * must keep the switcher as it was (the server still refuses an org it will not
 * serve — `<OrgScope/>` renders that 403), rather than turning every row into a
 * sign-in detour.
 */
export function isReachable(m: MembershipView): boolean {
  return m.reachable !== false;
}

/**
 * The membership an org reference names: the login case-insensitively (GitHub
 * logins are), or the account id. `undefined` when the user holds none.
 */
export function findMembership(
  memberships: readonly MembershipView[],
  org: string,
): MembershipView | undefined {
  const lower = org.toLowerCase();
  return memberships.find((m) => m.account_id === org || m.github_login?.toLowerCase() === lower);
}

/** The URL segment for a membership: its login when it has one, else the id. */
export function membershipSegment(m: MembershipView): string {
  return m.github_login ?? m.account_id;
}

/**
 * The full-page sign-in for an unreachable membership, carrying the dashboard
 * page to land on afterwards. `return_to` is ABSOLUTE on the dashboard origin:
 * the API's one validator (`apps/api/src/auth-return-to.ts`, see
 * `lib/return-to.ts`) refuses a bare path with a 400. `null` when the server
 * named no sign-in leg.
 */
export function signInHref(m: MembershipView, targetPath: string, origin: string): string | null {
  if (m.signIn === null) return null;
  return withReturnTo(m.signIn.url, `${origin}${targetPath}`);
}

/**
 * Why an org page this session was refused is NOT "you're not a member": the
 * user holds the membership, but this sign-in does not cover it (an SSO-pinned
 * session). `covers` is the logins the session DOES reach, `signIn` the denied
 * org's own leg back to `targetPath`. `null` when the user holds no membership
 * of `org` — the plain not-a-member page. Read off the session's own
 * memberships, so it tells the user nothing about an org they are not in.
 */
export type UncoveredOrg = { readonly covers: readonly string[]; readonly signIn: string | null };

export function uncoveredOrg(
  memberships: readonly MembershipView[],
  org: string,
  targetPath: string,
  origin: string,
): UncoveredOrg | null {
  const m = findMembership(memberships, org);
  if (m === undefined || isReachable(m)) return null;
  return {
    covers: memberships.filter(isReachable).map(membershipSegment),
    signIn: signInHref(m, targetPath, origin),
  };
}
